import { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Eye } from "lucide-react";
import type { SendInvitationEmailsResponse } from "@event-planner/shared";
import {
  EMAIL_STATE_COLOR,
  EMAIL_STATE_LABEL,
  NEEDS_EMAIL_STATES,
  type InvitationEmailState,
} from "../utils/invitationEmailState";

export interface BulkSendCandidate {
  invitationId: string;
  /** Roster-style "Last, First" name for the list. */
  displayName: string;
  /**
   * Name the email greets them by. Mirrors the server, which uses the
   * stored `persons.displayName` (first-name-first) or no name at all.
   */
  greetingName: string | null;
  email: string;
  /** Per-event code for new users; null for active users. */
  code: string | null;
  /** Active users receive the "You've Been Added" variant. */
  isActive: boolean;
  emailState: InvitationEmailState;
}

export type SendGroupKey = "newUsers" | "activeUsers";

const GROUP_ORDER: SendGroupKey[] = ["newUsers", "activeUsers"];

const GROUP_META: Record<SendGroupKey, { title: string; hint: string }> = {
  newUsers: {
    title: "New Users",
    hint:
      "Gets the New-User invitation email, including the reservation code they only need if they sign up with a different email address than the one invited.",
  },
  activeUsers: {
    title: "Active Users",
    hint:
      "Gets the Existing-User email. They already have the app, so there is no code; the email tells them they've been added and opens the event.",
  },
};

export function groupKeyFor(c: BulkSendCandidate): SendGroupKey {
  return c.isActive ? "activeUsers" : "newUsers";
}

export function groupCandidates(
  candidates: BulkSendCandidate[],
): Record<SendGroupKey, BulkSendCandidate[]> {
  const groups: Record<SendGroupKey, BulkSendCandidate[]> = {
    newUsers: [],
    activeUsers: [],
  };
  for (const c of candidates) groups[groupKeyFor(c)].push(c);
  return groups;
}

interface BulkSendConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * `bulk` — filter-driven list; nothing is selected until the organizer
   *   ticks people or groups.
   * `single` — one person from the row menu, pre-selected, same review gate.
   */
  mode: "bulk" | "single";
  candidates: BulkSendCandidate[];
  /** Opens the exact email this person would receive. */
  onPreview: (candidate: BulkSendCandidate) => void;
  /**
   * Fires with the selected invitation IDs. The dialog renders the resolved
   * response as a result screen and stays open until the organizer clicks
   * Done, so the parent should refetch inside this callback rather than
   * close the dialog.
   */
  onConfirm: (invitationIds: string[]) => Promise<SendInvitationEmailsResponse>;
}

const SKIP_REASON_LABEL: Record<
  SendInvitationEmailsResponse["skipped"][number]["reason"],
  string
> = {
  revoked: "no longer on the event",
  in_flight: "a send was already in progress",
  event_mismatch: "could not be matched to this event",
  not_found: "invitation record not found",
};

/**
 * Review-and-send dialog for invitation emails.
 *
 * Bulk mode lists the filter-visible rows in two groups, New Users and
 * Active Users, with a checkbox per person and a group checkbox that
 * toggles everyone in that group. Nothing is pre-selected: the organizer
 * chooses. Each row has a preview button that opens the exact email that
 * person would get. Confirm stays gated behind the review checkbox.
 */
export function BulkSendConfirmDialog({
  open,
  onClose,
  mode,
  candidates,
  onPreview,
  onConfirm,
}: BulkSendConfirmDialogProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendInvitationEmailsResponse | null>(null);

  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set());
      setAcknowledged(false);
      setSubmitting(false);
      setError(null);
      setResult(null);
    }
  }, [open]);

  const groups = useMemo(() => groupCandidates(candidates), [candidates]);

  const selected = useMemo(
    () =>
      mode === "single"
        ? candidates
        : candidates.filter((c) => selectedIds.has(c.invitationId)),
    [mode, candidates, selectedIds],
  );

  const single = mode === "single" ? candidates[0] ?? null : null;
  const singleIsResend = !!single && !NEEDS_EMAIL_STATES.has(single.emailState);

  const empty = candidates.length === 0;
  const nothingSelected = selected.length === 0;

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleGroup(key: SendGroupKey) {
    const ids = groups[key].map((c) => c.invitationId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      for (const id of ids) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  async function handleConfirm() {
    if (empty || nothingSelected || !acknowledged || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const response = await onConfirm(selected.map((c) => c.invitationId));
      setResult(response);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to send invitation emails.");
    } finally {
      setSubmitting(false);
    }
  }

  const title =
    mode === "single"
      ? singleIsResend
        ? "Resend invitation email"
        : "Send invitation email"
      : "Bulk invite";

  const confirmLabel =
    mode === "single"
      ? singleIsResend
        ? "Resend email"
        : "Send email"
      : `Send ${selected.length} ${selected.length === 1 ? "email" : "emails"}`;

  const renderRow = (c: BulkSendCandidate, withCheckbox: boolean) => {
    const checked = selectedIds.has(c.invitationId);
    return (
      <Box
        key={c.invitationId}
        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        data-testid={`bulk-send-row-${c.invitationId}`}
      >
        {withCheckbox && (
          <Checkbox
            size="small"
            checked={checked}
            onChange={() => toggleRow(c.invitationId)}
            sx={{ py: 0.25 }}
            inputProps={
              {
                "aria-label": `Include ${c.displayName}`,
                "data-testid": `bulk-send-row-checkbox-${c.invitationId}`,
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
          />
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.9rem",
              fontWeight: 500,
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {c.displayName}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: "text.secondary",
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {c.email}
          </Typography>
        </Box>
        <Chip
          label={EMAIL_STATE_LABEL[c.emailState]}
          size="small"
          color={EMAIL_STATE_COLOR[c.emailState]}
          variant="outlined"
          sx={{ fontSize: "0.75rem" }}
        />
        <Tooltip title="Preview this person's email">
          <IconButton
            size="small"
            onClick={() => onPreview(c)}
            aria-label={`Preview email for ${c.displayName}`}
            data-testid={`bulk-send-row-preview-${c.invitationId}`}
          >
            <Eye size={16} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const resultView = result && (
    <Stack spacing={1.5} data-testid="bulk-send-result">
      <Alert severity={result.sentCount > 0 ? "success" : "warning"}>
        {result.sentCount > 0
          ? `${result.sentCount} ${result.sentCount === 1 ? "email" : "emails"} handed to the mail service.`
          : "No emails were sent."}
      </Alert>
      {result.suppressedCount > 0 && (
        <Alert severity="warning">
          {result.suppressedCount} suppressed. Sending is switched off on the server
          (SUPPRESS_EMAILS). Nothing left the server for these people.
        </Alert>
      )}
      {result.skipped.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Skipped ({result.skipped.length})
          </Typography>
          <Stack spacing={0.25}>
            {result.skipped.map((s) => {
              const c = candidates.find((x) => x.invitationId === s.invitationId);
              return (
                <Typography key={s.invitationId} variant="body2" color="text.secondary">
                  {c?.displayName ?? s.invitationId}: {SKIP_REASON_LABEL[s.reason]}
                </Typography>
              );
            })}
          </Stack>
        </Box>
      )}
      {result.sentCount > 0 && (
        <Typography variant="body2" color="text.secondary">
          Delivery status updates in the table over the next minute or two. A
          “Failed” chip means the mail service rejected the address; use the row
          menu to retry.
        </Typography>
      )}
    </Stack>
  );

  const visibleGroups = GROUP_ORDER.filter((key) => groups[key].length > 0);

  const groupList = (
    <Paper variant="outlined" sx={{ maxHeight: 440, overflow: "auto", px: 2, py: 1, mb: 2 }}>
      {visibleGroups.map((key, index) => {
        const members = groups[key];
        const selectedCount = members.filter((c) => selectedIds.has(c.invitationId)).length;
        const all = selectedCount === members.length;
        const some = selectedCount > 0 && !all;
        return (
          <Box key={key} data-testid={`bulk-send-group-${key}`}>
            {index > 0 && <Divider sx={{ my: 2 }} />}
            <FormControlLabel
              sx={{ alignItems: "flex-start", ml: 0, mt: 1, mb: 1.5 }}
              control={
                <Checkbox
                  checked={all}
                  indeterminate={some}
                  onChange={() => toggleGroup(key)}
                  sx={{ py: 0 }}
                  inputProps={
                    {
                      "data-testid": `bulk-send-group-checkbox-${key}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {GROUP_META[key].title} ({selectedCount}/{members.length})
                  </Typography>
                  <Typography variant="body1" sx={{ color: "text.secondary", mt: 0.5 }}>
                    {GROUP_META[key].hint}
                  </Typography>
                </Box>
              }
            />
            <Stack spacing={0.75} sx={{ pl: 5, pb: 1 }}>
              {members.map((c) => renderRow(c, true))}
            </Stack>
          </Box>
        );
      })}
    </Paper>
  );

  const reviewView = (
    <>
      {mode === "single" && single ? (
        <DialogContentText sx={{ mb: 2 }} component="div">
          {singleIsResend
            ? "Send another copy of the invitation email to:"
            : "Send the invitation email to:"}
          <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5 }}>
            {renderRow(single, false)}
          </Paper>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {single.isActive
              ? "This person is an existing user, so they receive the “You've Been Added” email without a code."
              : "This person is a new user, so they receive the invitation email with their reservation code."}
          </Typography>
        </DialogContentText>
      ) : (
        <>
          <DialogContentText sx={{ mb: 2 }}>
            Tick the people to email, or use a group checkbox to select everyone in
            that group. Use the eye icon to see exactly what each person receives.
          </DialogContentText>
          {groupList}
        </>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            data-testid="bulk-send-acknowledge-checkbox"
          />
        }
        label={
          mode === "single"
            ? "I've confirmed this is the right recipient."
            : "I've reviewed this list and confirm these are the right recipients."
        }
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      data-testid="bulk-send-confirm-dialog"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {result ? (
          resultView
        ) : empty ? (
          <DialogContentText>
            No invitations match the current filters. Adjust the filters to
            target the recipients you want to email.
          </DialogContentText>
        ) : (
          reviewView
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button variant="contained" onClick={onClose} data-testid="bulk-send-done-button">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={empty || nothingSelected || !acknowledged || submitting}
              data-testid="bulk-send-confirm-button"
              startIcon={submitting ? <CircularProgress size={16} /> : undefined}
            >
              {submitting ? "Sending…" : confirmLabel}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
